import React from 'react';
import {
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  TrendingDown,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CapitulationTableSkeleton } from './ReversalCatcherSkeleton';

const CapitulationList = ({ stocks, onRefresh, loading = false }) => {
  const { t } = useTranslation();

  if (loading && (!stocks || stocks.length === 0)) {
    return <CapitulationTableSkeleton rows={8} />;
  }
  
  if (!stocks || stocks.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <TrendingDown sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#64748b', mb: 0.5 }}>
          {t('reversalCatcher.capitulation.emptyTitle')}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
          {t('reversalCatcher.capitulation.emptyDesc')}
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Summary chip */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.85 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}>
          {t('reversalCatcher.capitulation.foundTitle', { count: stocks.length })}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
          {t('reversalCatcher.capitulation.foundHint')}
        </Typography>
      </Box>
      
      {/* Table */}
      <Box sx={{ 
        background: '#ffffff', 
        border: '1px solid #e5e7eb', 
        borderRadius: 2, 
        overflow: 'hidden' 
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 0.8fr 1fr 0.8fr 1fr 1fr 0.8fr',
          p: '8px 12px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          borderBottom: '1px solid #e2e8f0'
        }}>
          {[
            t('common.ticker'),
            t('reversalCatcher.capitulation.capScore'),
            t('reversalCatcher.capitulation.price'),
            t('reversalCatcher.capitulation.rsi'),
            t('reversalCatcher.capitulation.ma20'),
            t('reversalCatcher.capitulation.liquidity'),
            t('common.tags'),
          ].map((h, i) => (
            <Typography key={i} sx={{ 
              fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', 
              letterSpacing: '0.3px', color: '#64748b',
              textAlign: i >= 2 && i <= 5 ? 'right' : i === 6 ? 'center' : 'left'
            }}>
              {h}
            </Typography>
          ))}
        </Box>
        
        {/* Rows */}
        {stocks.map((stock, idx) => {
          const scoreColor = stock.cap_score >= 3 ? '#ef4444' : stock.cap_score >= 2 ? '#f59e0b' : '#94a3b8';
          
          return (
            <Box key={stock.ticker} sx={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 0.8fr 1fr 0.8fr 1fr 1fr 0.8fr',
              p: '7px 12px',
              borderBottom: '1px solid #f8fafc',
              borderLeft: `3px solid ${scoreColor}`,
              transition: 'background 0.15s',
              '&:hover': { background: '#f0f9ff' },
              ...(idx % 2 === 1 ? { bgcolor: '#fafbfc' } : {})
            }}>
              {/* Ticker */}
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                {stock.ticker}
              </Typography>
              
              {/* Cap Score */}
              <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
                {[1, 2, 3].map(i => (
                  <Box key={i} sx={{
                    width: 7, height: 7, borderRadius: '50%',
                    bgcolor: i <= stock.cap_score ? scoreColor : '#e5e7eb',
                  }} />
                ))}
                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', ml: 0.3 }}>
                  {t('reversalCatcher.capitulation.scoreOf', { score: stock.cap_score })}
                </Typography>
              </Box>
              
              {/* Price */}
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b', textAlign: 'right', fontFamily: 'monospace' }}>
                {stock.current_price?.toLocaleString()}
              </Typography>
              
              {/* RSI */}
              <Typography sx={{ 
                fontSize: '0.78rem', fontWeight: 600, textAlign: 'right', fontFamily: 'monospace',
                color: stock.rsi14 < 30 ? '#dc2626' : stock.rsi14 < 40 ? '#f59e0b' : '#475569'
              }}>
                {stock.rsi14 != null ? stock.rsi14.toFixed(1) : '—'}
              </Typography>
              
              {/* MA20 discount */}
              <Typography sx={{ 
                fontSize: '0.78rem', fontWeight: 600, textAlign: 'right', fontFamily: 'monospace',
                color: stock.discount_ma20_pct < -15 ? '#dc2626' : stock.discount_ma20_pct < -5 ? '#f59e0b' : '#475569'
              }}>
                {stock.discount_ma20_pct != null ? `${stock.discount_ma20_pct.toFixed(1)}%` : '—'}
              </Typography>
              
              {/* Liquidity */}
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, textAlign: 'right', fontFamily: 'monospace', color: '#475569' }}>
                {stock.avg_value_20d_bn != null
                  ? t('reversalCatcher.capitulation.liquidityBn', { value: stock.avg_value_20d_bn.toFixed(1) })
                  : '—'}
              </Typography>
              
              {/* Tags */}
              <Box sx={{ textAlign: 'center' }}>
                {stock.tier1_ok ? (
                  <Chip label={t('reversalCatcher.tags.quality')} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#10b981', color: '#fff', '& .MuiChip-label': { px: 0.75 } }} />
                ) : stock.is_capitulation ? (
                  <Chip label={t('reversalCatcher.tags.hot')} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#ef4444', color: '#fff', '& .MuiChip-label': { px: 0.75 } }} />
                ) : stock.tier2_ok ? (
                  <Chip label={t('reversalCatcher.tags.ok')} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b', '& .MuiChip-label': { px: 0.75 } }} />
                ) : (
                  <Typography sx={{ fontSize: '0.7rem', color: '#cbd5e1' }}>—</Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CapitulationList;
